import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSplitBudgetLineToExternal } from "@/hooks/use-budget-lines";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { formatNOK } from "@/lib/format";

interface Props {
  budgetLine: BudgetLineRow | null;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Splitter en intern Oppgave-linje til Oppgave + Kostnad. Admin spesifiserer
 * hvor mange timer / hvor mye beløp som flyttes til en ny ekstern Kostnad-linje.
 * Eksisterende assignments på Oppgave-delen beholdes uendret.
 */
export default function SplitBudgetLineDialog({
  budgetLine: bl,
  projectId,
  open,
  onOpenChange,
}: Props) {
  const splitMut = useSplitBudgetLineToExternal();
  const [internalHours, setInternalHours] = useState("");
  const [internalAmount, setInternalAmount] = useState("");
  const [externalDesc, setExternalDesc] = useState("");
  const [externalAmount, setExternalAmount] = useState("");
  const [externalCost, setExternalCost] = useState("");

  useEffect(() => {
    if (open && bl) {
      const totalHours = bl.quantity_hours ?? 0;
      const totalAmount = bl.amount ?? 0;
      const halfHours = Math.floor(totalHours / 2);
      const halfAmount = totalHours > 0
        ? Math.round((halfHours / totalHours) * totalAmount)
        : Math.round(totalAmount / 2);
      setInternalHours(String(halfHours));
      setInternalAmount(String(halfAmount));
      setExternalDesc(`${bl.description ?? ""} — ekstern`);
      setExternalAmount(String(totalAmount - halfAmount));
      setExternalCost("0");
    }
  }, [open, bl]);

  if (!bl) return null;

  const totalHours = bl.quantity_hours ?? 0;
  const totalAmount = bl.amount ?? 0;
  const intH = parseFloat(internalHours);
  const intA = parseFloat(internalAmount);
  const extA = parseFloat(externalAmount);
  const extC = parseFloat(externalCost);

  const valid =
    !isNaN(intH) && intH >= 0 && intH <= totalHours &&
    !isNaN(intA) && intA >= 0 && intA <= totalAmount &&
    !isNaN(extA) && extA >= 0 &&
    !isNaN(extC) && extC >= 0 &&
    externalDesc.trim().length > 0;

  const canSubmit = valid && !splitMut.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await splitMut.mutateAsync({
        source_id: bl.id,
        project_id: projectId,
        internal: { quantity_hours: intH, amount: intA },
        external: {
          description: externalDesc.trim(),
          amount: extA,
          estimated_cost: extC,
        },
      });
      onOpenChange(false);
    } catch {
      /* toast håndteres i hook */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Splitt linje i Oppgave + Kostnad</DialogTitle>
          <DialogDescription>
            {bl.description} — {totalHours}t / {formatNOK(totalAmount)}. Oppgave-delen
            beholdes som intern timepost med redusert sum, og resten flyttes til en
            ny ekstern Kostnad-linje.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="border-[1.5px] border-foreground/40 rounded p-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Oppgave (beholdes intern)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Timer</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={internalHours}
                  onChange={(e) => setInternalHours(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Beløp (kr)</Label>
                <Input
                  type="number"
                  step="100"
                  value={internalAmount}
                  onChange={(e) => setInternalAmount(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="border-[1.5px] border-foreground/40 rounded p-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Kostnad (ny ekstern linje)
            </div>
            <div>
              <Label className="text-xs">Beskrivelse</Label>
              <Input
                value={externalDesc}
                onChange={(e) => setExternalDesc(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Beløp (kunde, kr)</Label>
                <Input
                  type="number"
                  step="100"
                  value={externalAmount}
                  onChange={(e) => setExternalAmount(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Estimert kostnad (vår, kr)</Label>
                <Input
                  type="number"
                  step="100"
                  value={externalCost}
                  onChange={(e) => setExternalCost(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {splitMut.isPending ? "Splitter..." : "Splitt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
